import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Facebook, Linkedin, Link2, Loader2, PawPrint } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Reveal, Eyebrow } from "../components/Reveal";
import { fmtDate } from "./Blog";

const XIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

function renderBody(body) {
  const lines = (body || "").split("\n");
  const blocks = [];
  let list = [];
  const flushList = (key) => {
    if (list.length) {
      blocks.push(
        <ul key={key} className="my-5 space-y-2.5">
          {list.map((li, i) => (
            <li key={i} className="flex gap-3 text-slate-300 leading-relaxed">
              <PawPrint className="h-4 w-4 text-brand-crimson shrink-0 mt-1" />
              <span>{li}</span>
            </li>
          ))}
        </ul>
      );
      list = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (line.startsWith("- ")) {
      list.push(line.slice(2));
      return;
    }
    flushList(`ul-${idx}`);
    if (!line) return;
    if (line.startsWith("### ")) {
      blocks.push(<h3 key={idx} className="mt-8 mb-2 font-display text-lg sm:text-xl font-bold text-white">{line.slice(4)}</h3>);
    } else if (line.startsWith("## ")) {
      blocks.push(<h2 key={idx} className="mt-10 mb-3 font-display text-2xl sm:text-3xl font-bold text-white">{line.slice(3)}</h2>);
    } else {
      blocks.push(<p key={idx} className="my-5 text-slate-300 leading-relaxed">{line}</p>);
    }
  });
  flushList("ul-end");
  return blocks;
}

function ShareBar({ title }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const open = (href) => window.open(href, "_blank", "noopener,noreferrer,width=640,height=560");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied - paste it anywhere");
    } catch {
      toast.error("Could not copy the link");
    }
  };
  const btn =
    "inline-flex items-center gap-2 rounded-md border border-brand-line bg-brand-surface px-4 py-2.5 text-[11px] font-eyebrow uppercase tracking-[0.15em] font-bold text-white/80 transition-all duration-300 hover:border-brand-crimson/60 hover:text-white";
  return (
    <div className="flex flex-wrap items-center gap-2.5" data-testid="share-bar">
      <span className="text-[11px] font-eyebrow uppercase tracking-[0.25em] text-white/40 font-bold mr-1">Share</span>
      <button
        className={btn}
        data-testid="share-facebook"
        onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)}
      >
        <Facebook className="h-4 w-4 text-[#1877F2]" /> Facebook
      </button>
      <button
        className={btn}
        data-testid="share-x"
        onClick={() => open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title || "")}`)}
      >
        <XIcon className="h-3.5 w-3.5" /> X
      </button>
      <button
        className={btn}
        data-testid="share-whatsapp"
        onClick={() => open(`https://wa.me/?text=${encodeURIComponent(`${title || ""} ${url}`)}`)}
      >
        <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> WhatsApp
      </button>
      <button
        className={btn}
        data-testid="share-linkedin"
        onClick={() => open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`)}
      >
        <Linkedin className="h-4 w-4 text-[#0A66C2]" /> LinkedIn
      </button>
      <button className={btn} data-testid="share-copy-link" onClick={copy}>
        <Link2 className="h-4 w-4 text-brand-crimson" /> Copy Link
      </button>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} | Quest K9's Detection Dogs`;
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="bg-brand-obsidian min-h-screen pt-28 sm:pt-32 flex flex-col items-center justify-center text-center px-4 pb-24" data-testid="blog-post-not-found">
        <h1 className="font-display text-3xl font-bold text-white">Post not found</h1>
        <p className="mt-3 text-slate-400 text-sm">This post may have been moved or unpublished.</p>
        <Link
          to="/blog"
          data-testid="back-to-blog-from-404"
          className="mt-6 inline-flex items-center gap-2 bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-xs uppercase tracking-[0.2em] font-bold px-6 py-3 rounded-md transition-all duration-300"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Blog
        </Link>
      </main>
    );
  }

  return (
    <main className="bg-brand-obsidian min-h-screen pt-28 sm:pt-32" data-testid="blog-post-page">
      <article className="px-4 sm:px-6 pb-24">
        <div className="mx-auto max-w-3xl">
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="inline-flex items-center gap-2 text-[11px] font-eyebrow uppercase tracking-[0.2em] font-bold text-white/50 hover:text-brand-crimson transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All Posts
          </Link>

          {post === null && (
            <div className="flex justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="blog-post-loading-spinner" />
            </div>
          )}

          {post && (
            <>
              <Eyebrow className="mt-8 flex items-center gap-3">
                <span className="h-px w-10 bg-brand-crimson inline-block" /> {fmtDate(post.published_at || post.created_at)}
              </Eyebrow>
              <h1 className="mt-4 font-display font-extrabold text-3xl sm:text-5xl text-white tracking-tight leading-tight" data-testid="blog-post-title">
                {post.title}
              </h1>
              {post.excerpt && <p className="mt-4 text-lg text-slate-400 leading-relaxed">{post.excerpt}</p>}

              {post.cover_image && (
                <Reveal className="mt-8">
                  <img src={post.cover_image} alt={post.title} className="w-full rounded-lg border border-brand-line object-cover max-h-[460px]" data-testid="blog-post-cover" />
                </Reveal>
              )}

              <Reveal className="mt-6">
                <div data-testid="blog-post-body">{renderBody(post.body)}</div>
              </Reveal>

              <div className="mt-12 border-t border-brand-line pt-8">
                <ShareBar title={post.title} />
              </div>
            </>
          )}
        </div>
      </article>
    </main>
  );
}
